import { useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Paper, Stack, TextField, Typography, Divider,
  ToggleButton, ToggleButtonGroup, MenuItem,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  tenantsApi, type TenantSettingsDto, type ServiceAccountMappingMode, type InvoiceRoundingMode,
} from "@/api/endpoints";
import { PageHeader } from "@/components/PageHeader";
import { errorMessage } from "@/api/client";
import { formatDate } from "@/shared/datetime";
import { useAuth } from "@/store/auth";

/**
 * Tenant settings. Mostly read-only: the headline value is the tax regime, shown alongside
 * identity and a clear note that switching regimes post-go-live is a Finance-ticketed migration.
 *
 * The one setting that IS changeable here is the service account-head mapping — already-posted
 * journal entries keep the accounts they were posted to, so only future postings follow a change.
 *
 * Path: /admin/tenant-settings (registered in App.tsx).
 */
export function TenantSettingsPage() {
  const q = useQuery({
    queryKey: ["tenant", "current"],
    queryFn: tenantsApi.current,
    staleTime: 5 * 60_000,
  });

  if (q.isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (q.error || !q.data) {
    return <Alert severity="error" sx={{ m: 2 }}>{errorMessage(q.error)}</Alert>;
  }

  const t = q.data;
  return (
    <Box sx={{ p: 3, maxWidth: 720 }}>
      <PageHeader breadcrumbs={["Administration"]} title="Tenant settings" />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Tenant identity and regime configuration. Operational data (branches,
        warehouses, users, feature flags) is managed in the dedicated pages.
      </Typography>

      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="overline" color="text.secondary">Identity</Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={4} sx={{ mt: 1 }}>
          <Field label="Name" value={t.name} />
          <Field label="Code" value={t.code} />
          <Field label="Status">
            <Chip size="small"
              color={t.isActive ? "success" : "default"}
              label={t.isActive ? "Active" : "Inactive"} />
          </Field>
          <Field label="Created" value={formatDate(t.createdAt)} />
        </Stack>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Typography variant="overline" color="text.secondary">Tax regime</Typography>
          <LockIcon fontSize="small" sx={{ color: "text.disabled" }} />
        </Stack>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={4} alignItems="flex-start">
          <Stack>
            <RegimeChip regime={t.taxRegime} />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              {regimeDescription(t.taxRegime)}
            </Typography>
          </Stack>
        </Stack>

        <Divider sx={{ my: 2 }} />
        <Alert severity="info" variant="outlined">
          <Typography variant="body2">
            <strong>Changing the tax regime is not a UI action.</strong> Switching after
            go-live requires migrating historical journal entries, tax-account balances,
            and customer/vendor tax identifiers. Open a Finance ticket and the migration
            runs out of band.
          </Typography>
        </Alert>
      </Paper>

      <ServiceAccountMappingCard tenant={t} />
      <DiscountLimitsCard tenant={t} />
      <ReturnPolicyCard tenant={t} />
      <InvoiceRoundingCard tenant={t} />
    </Box>
  );
}

/**
 * The account-head mapping switch. Unlike the tax regime this one is safe to change after
 * go-live, so it is an actual control — permission-gated on accounting.account.manage and
 * audited server-side. The API refuses the switch to group mapping while any group holding
 * service items has no account head; that error surfaces here verbatim.
 */
function ServiceAccountMappingCard({ tenant }: { tenant: TenantSettingsDto }) {
  const qc = useQueryClient();
  const canManage = useAuth(s => s.hasPermission("accounting.account.manage"));
  const [pending, setPending] = useState<ServiceAccountMappingMode | null>(null);

  const mutation = useMutation({
    meta: { successMessage: "Service account mapping updated" },
    mutationFn: (mode: ServiceAccountMappingMode) => tenantsApi.setServiceAccountMapping(mode),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tenant", "current"] });
      qc.invalidateQueries({ queryKey: ["services"] });
      setPending(null);
    },
  });

  const current = tenant.serviceAccountMappingMode;
  const selected = pending ?? current;
  const dirty = selected !== current;

  return (
    <Paper sx={{ p: 3, mt: 2 }}>
      <Typography variant="overline" color="text.secondary">Service account mapping</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
        Where a service line reads its revenue account head from. Some clients maintain the chart
        of accounts on each service item; others map it once per service group.
      </Typography>

      {mutation.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(mutation.error)}</Alert>
      )}

      <ToggleButtonGroup
        exclusive
        value={selected}
        onChange={(_, v: ServiceAccountMappingMode | null) => v && setPending(v)}
        disabled={!canManage || mutation.isPending}
        sx={{ mb: 2, flexWrap: "wrap" }}
      >
        <ToggleButton value="ServiceItem" sx={{ textTransform: "none", px: 2 }}>
          <Stack alignItems="flex-start">
            <Typography variant="body2" fontWeight={600}>Service item</Typography>
            <Typography variant="caption" color="text.secondary">Head mapped on each service</Typography>
          </Stack>
        </ToggleButton>
        <ToggleButton value="ServiceGroup" sx={{ textTransform: "none", px: 2 }}>
          <Stack alignItems="flex-start">
            <Typography variant="body2" fontWeight={600}>Service group</Typography>
            <Typography variant="caption" color="text.secondary">Head mapped once per group</Typography>
          </Stack>
        </ToggleButton>
      </ToggleButtonGroup>

      {dirty && (
        <Alert
          severity="warning" sx={{ mb: 2 }}
          action={
            <Stack direction="row" spacing={1}>
              <Button size="small" onClick={() => setPending(null)}>Cancel</Button>
              <Button size="small" variant="contained" disabled={mutation.isPending}
                onClick={() => mutation.mutate(selected)}>
                {mutation.isPending ? <CircularProgress size={16} color="inherit" /> : "Apply"}
              </Button>
            </Stack>
          }
        >
          {selected === "ServiceGroup"
            ? "Every service group holding service items will need an account head. The change is rejected if any is unmapped."
            : "Service items will post to their own account head. Groups with services but no head are no longer a problem."}
        </Alert>
      )}

      {!canManage && (
        <Typography variant="caption" color="text.secondary">
          Requires the accounting.account.manage permission to change.
        </Typography>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
        Already-posted journal entries keep the accounts they were posted to — only future service
        postings follow the new mode. A service with nothing mapped on either side falls back to
        the default 4300 “Service Revenue” head.
      </Typography>
    </Paper>
  );
}

/**
 * W4 — how much of a bill each tier may give away.
 *
 * Since the server started pricing every line, the discount fields are the only remaining way to
 * change what a patient pays, so they are what needs an authority behind them. Two ceilings: one
 * for anyone who can raise a bill, one for a holder of `sales.discount.override`. Anything past
 * the elevated ceiling is refused outright — there is no second-approver workflow yet.
 */
function DiscountLimitsCard({ tenant }: { tenant: TenantSettingsDto }) {
  const qc = useQueryClient();
  const canManage = useAuth(s => s.hasPermission("sales.discount.override"));
  const [standard, setStandard] = useState<number>(tenant.standardDiscountLimitPct);
  const [elevated, setElevated] = useState<number>(tenant.elevatedDiscountLimitPct);

  const mutation = useMutation({
    meta: { successMessage: "Discount limits updated" },
    mutationFn: () => tenantsApi.setDiscountLimits(standard, elevated),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tenant", "current"] }),
  });

  const dirty = standard !== tenant.standardDiscountLimitPct
    || elevated !== tenant.elevatedDiscountLimitPct;
  const inverted = elevated < standard;
  const outOfRange = [standard, elevated].some(v => v < 0 || v > 100);

  return (
    <Paper sx={{ p: 3, mt: 2 }}>
      <Typography variant="overline" color="text.secondary">Discount limits</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
        The most a bill may be discounted, as a percentage of its gross. Checked per line and
        across the whole bill, so neither route around the other is open.
      </Typography>

      {mutation.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(mutation.error)}</Alert>
      )}

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          type="number" size="small" label="Standard (%)" value={standard}
          onChange={(e) => setStandard(Number(e.target.value))}
          disabled={!canManage || mutation.isPending}
          helperText="Anyone who can raise a bill"
          inputProps={{ min: 0, max: 100, step: "0.5" }} sx={{ width: 200 }}
        />
        <TextField
          type="number" size="small" label="Elevated (%)" value={elevated}
          onChange={(e) => setElevated(Number(e.target.value))}
          disabled={!canManage || mutation.isPending}
          error={inverted}
          helperText={inverted
            ? "Cannot be below the standard limit"
            : "Holders of sales.discount.override"}
          inputProps={{ min: 0, max: 100, step: "0.5" }} sx={{ width: 200 }}
        />
      </Stack>

      {dirty && canManage && (
        <Alert
          severity="warning" sx={{ mb: 2 }}
          action={
            <Stack direction="row" spacing={1}>
              <Button size="small" onClick={() => {
                setStandard(tenant.standardDiscountLimitPct);
                setElevated(tenant.elevatedDiscountLimitPct);
              }}>Cancel</Button>
              <Button size="small" variant="contained"
                disabled={mutation.isPending || inverted || outOfRange}
                onClick={() => mutation.mutate()}>
                {mutation.isPending ? <CircularProgress size={16} color="inherit" /> : "Apply"}
              </Button>
            </Stack>
          }
        >
          Raising a ceiling takes effect on the next bill. Bills already raised keep the discount
          they were approved at.
        </Alert>
      )}

      {!canManage && (
        <Typography variant="caption" color="text.secondary">
          Requires the sales.discount.override permission to change — the same authority as
          approving a large discount, granted once rather than per bill.
        </Typography>
      )}
    </Paper>
  );
}

/**
 * W8 — what the counter may take back against a bill.
 *
 * Schedule-drug returns default to off. Once a Schedule H/H1/X medicine has left the counter its
 * chain of custody is broken and in Indian practice it is generally not returnable to sellable
 * stock, so turning this on is a deliberate regulatory posture rather than a convenience toggle.
 */
function ReturnPolicyCard({ tenant }: { tenant: TenantSettingsDto }) {
  const qc = useQueryClient();
  const canManage = useAuth(s => s.hasPermission("sales.return.post"));
  const [days, setDays] = useState<number>(tenant.returnWindowDays);
  const [allowSchedule, setAllowSchedule] = useState<boolean>(tenant.allowScheduleDrugReturns);

  const mutation = useMutation({
    meta: { successMessage: "Return policy updated" },
    mutationFn: () => tenantsApi.setReturnPolicy(days, allowSchedule),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tenant", "current"] }),
  });

  const dirty = days !== tenant.returnWindowDays
    || allowSchedule !== tenant.allowScheduleDrugReturns;
  const outOfRange = days < 0 || days > 365;

  return (
    <Paper sx={{ p: 3, mt: 2 }}>
      <Typography variant="overline" color="text.secondary">Return policy</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
        How long after a bill goods may come back, and whether schedule drugs may come back at
        all. Expired batches are never returnable — those are a write-off, not a return.
      </Typography>

      {mutation.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(mutation.error)}</Alert>
      )}

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="flex-start" sx={{ mb: 2 }}>
        <TextField
          type="number" size="small" label="Return window (days)" value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          disabled={!canManage || mutation.isPending}
          error={outOfRange}
          helperText={outOfRange ? "0 to 365" : "0 means same-day only"}
          inputProps={{ min: 0, max: 365, step: 1 }} sx={{ width: 220 }}
        />
        <ToggleButtonGroup
          exclusive
          value={allowSchedule ? "allow" : "block"}
          onChange={(_, v: string | null) => v && setAllowSchedule(v === "allow")}
          disabled={!canManage || mutation.isPending}
          sx={{ flexWrap: "wrap" }}
        >
          <ToggleButton value="block" sx={{ textTransform: "none", px: 2 }}>
            <Stack alignItems="flex-start">
              <Typography variant="body2" fontWeight={600}>Block schedule drugs</Typography>
              <Typography variant="caption" color="text.secondary">Chain of custody broken</Typography>
            </Stack>
          </ToggleButton>
          <ToggleButton value="allow" sx={{ textTransform: "none", px: 2 }}>
            <Stack alignItems="flex-start">
              <Typography variant="body2" fontWeight={600}>Allow schedule drugs</Typography>
              <Typography variant="caption" color="text.secondary">Only if your policy permits</Typography>
            </Stack>
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {dirty && canManage && (
        <Alert
          severity={allowSchedule && !tenant.allowScheduleDrugReturns ? "warning" : "info"}
          sx={{ mb: 2 }}
          action={
            <Stack direction="row" spacing={1}>
              <Button size="small" onClick={() => {
                setDays(tenant.returnWindowDays);
                setAllowSchedule(tenant.allowScheduleDrugReturns);
              }}>Cancel</Button>
              <Button size="small" variant="contained"
                disabled={mutation.isPending || outOfRange}
                onClick={() => mutation.mutate()}>
                {mutation.isPending ? <CircularProgress size={16} color="inherit" /> : "Apply"}
              </Button>
            </Stack>
          }
        >
          {allowSchedule && !tenant.allowScheduleDrugReturns
            ? "Schedule drugs returned to stock can be dispensed again. Confirm your licence permits this before enabling it."
            : "Applies to returns raised from now on. Credit notes already posted are unaffected."}
        </Alert>
      )}

      {!canManage && (
        <Typography variant="caption" color="text.secondary">
          Requires the sales.return.post permission to change.
        </Typography>
      )}
    </Paper>
  );
}

function Field({ label, value, children }: { label: string; value?: string; children?: React.ReactNode }) {
  return (
    <Stack>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      {value !== undefined ? <Typography variant="body1">{value}</Typography> : children}
    </Stack>
  );
}

function RegimeChip({ regime }: { regime: TenantSettingsDto["taxRegime"] }) {
  const map = {
    Gst:  { label: "India GST",      color: "primary"   as const },
    Vat:  { label: "VAT",            color: "secondary" as const },
    None: { label: "No statutory tax", color: "default" as const },
  };
  const m = map[regime];
  return <Chip color={m.color} label={m.label} sx={{ fontWeight: 600 }} />;
}

function regimeDescription(regime: TenantSettingsDto["taxRegime"]): string {
  switch (regime) {
    case "Gst":
      return "CGST/SGST intra-state, IGST inter-state. GL accounts 1410 (input) / 2200 (output). Eligible for IRP e-invoice and e-way bill integrations.";
    case "Vat":
      return "Single-component VAT. GL accounts 1411 (input) / 2210 (output). Jurisdiction-specific filing wired per customer (UAE FTA, KSA ZATCA, UK HMRC, EU OSS).";
    case "None":
      return "Template tenant or jurisdiction without statutory tax. Invoices post sub-total and AR/AP only; no tax line is generated.";
  }
}

const ROUNDING_LABEL: Record<InvoiceRoundingMode, string> = {
  None: "No rounding — billed to the paisa",
  Nearest: "Nearest rupee",
  Up: "Always up",
  Down: "Always down",
};

const ROUNDING_HINT: Record<InvoiceRoundingMode, string> = {
  None: "What every tenant did before this setting existed. Counters reconcile to the paisa.",
  Nearest: "Halves go up. The ordinary retail rule.",
  Up: "The hospital never gives up the fraction; the patient always pays it.",
  Down: "Always in the patient's favour.",
};

/**
 * W24 — how bills are rounded before they are asked for.
 *
 * <p>Behind the posting permission rather than a generic admin one: this decides what money the
 * counter asks for, from the next bill onwards. The difference is recorded on every invoice and
 * posts to its own ledger head, which is the whole reason to make it a rule rather than let a
 * cashier absorb it.</p>
 */
function InvoiceRoundingCard({ tenant }: { tenant: TenantSettingsDto }) {
  const qc = useQueryClient();
  const canManage = useAuth(s => s.hasPermission("sales.invoice.post"));
  const [mode, setMode] = useState<InvoiceRoundingMode>(tenant.invoiceRoundingMode);

  const mutation = useMutation({
    meta: { successMessage: "Invoice rounding updated" },
    mutationFn: () => tenantsApi.setInvoiceRounding(mode),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tenant", "current"] }),
  });

  const dirty = mode !== tenant.invoiceRoundingMode;

  return (
    <Paper sx={{ p: 3, mb: 2 }}>
      <Typography variant="subtitle1" sx={{ mb: 0.5 }}>Invoice rounding</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Rounding is to the whole rupee. The difference is recorded on the bill and posted to
        <b> 5200 Rounding Off</b>, so it is never absorbed into revenue or into somebody&apos;s till.
      </Typography>

      {mutation.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(mutation.error)}</Alert>
      )}

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="flex-start">
        <TextField select label="Rule" size="small" sx={{ minWidth: 280 }}
          value={mode} disabled={!canManage}
          onChange={(e) => setMode(e.target.value as InvoiceRoundingMode)}
          helperText={ROUNDING_HINT[mode]}>
          {(Object.keys(ROUNDING_LABEL) as InvoiceRoundingMode[]).map(m => (
            <MenuItem key={m} value={m}>{ROUNDING_LABEL[m]}</MenuItem>
          ))}
        </TextField>

        {canManage && (
          <Button variant="contained" disabled={!dirty || mutation.isPending}
            onClick={() => mutation.mutate()}>
            Save
          </Button>
        )}
      </Stack>

      {dirty && mode !== "None" && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          This applies from the next bill raised. Every patient is asked for a slightly different
          figure than they would have been today.
        </Alert>
      )}
    </Paper>
  );
}
